// Admin: Sitemap — every real screen in the prototype, as a clickable diagram (not a text list).
// Mirrors the sidebar groups in components/shell.js and the routes registered in app.js.
// Curated by hand: tabs/query-param variants of a page don't show up as separate routes, so an
// auto-parse of the router would either miss them or explode into noise — a short caption does the job.
import { html } from '../../lib/dom';
import { pageHead } from '../../components/ui';
import { diagramBox, diagramCanvas, diagramGroupLabel, diagramLegend, edgeH } from '../../components/diagram';

const COL_W = 250, ROW_H = 96, BOX_W = 208, BOX_H = 68, PAD = 30;

const TREE = [
  { label: '', items: [
    { title: 'Home', href: '#/', icon: 'home', sub: 'Dashboard: continue working, recents, favorites' },
  ] },
  { label: 'Work', items: [
    { title: 'Projects', href: '#/projects', icon: 'projects', sub: 'All / Recent / Favorites / Mine / Active / Archived', children: [
      { title: 'Project', href: '#/projects/prj_koloblox_website', icon: 'projects', sub: 'Overview · Flows · Assets · Runs · Data · Settings', children: [
        { title: 'Flow canvas', href: '#/projects/prj_koloblox_website/flows/flw_kw_hero_scene_generator', icon: 'node', sub: 'Nodes, groups, ▶ Run, inspector' },
      ] },
    ] },
    { title: 'Workspaces', href: '#/workspaces', icon: 'workspaces', sub: 'People, permissions, defaults', children: [
      { title: 'Workspace', href: '#/workspaces/ws_creative_studio', icon: 'workspaces', sub: 'Overview · Projects · Assets · Members · Settings' },
    ] },
  ] },
  { label: 'Resources', items: [
    { title: 'Assets', href: '#/assets', icon: 'archive', sub: 'One registry: types, scopes, filters', children: [
      { title: 'Asset', href: '#/assets/ast_koloblox_mascot_v5', icon: 'library', sub: 'Belongs to · Used in · Versions · Activity' },
    ] },
    { title: 'Brands', href: '#/brands', icon: 'brands', sub: 'Global, cross-workspace context', children: [
      { title: 'Brand', href: '#/brands/brd_koloblox', icon: 'brands', sub: 'Overview · Identity · Assets · Projects · Usage' },
    ] },
  ] },
  { label: 'Activity', items: [
    { title: 'Runs', href: '#/runs', icon: 'runs', sub: 'Global execution history', children: [
      { title: 'Run', href: '#/runs/run_1384', icon: 'runs', sub: 'Timeline, node input/output, retry' },
    ] },
  ] },
  { label: '', items: [
    { title: 'Settings', href: '#/settings', icon: 'settings', sub: 'General · Members · Connections · Variables · Usage · Billing' },
  ] },
  { label: 'Community', items: [
    { title: 'Gallery', href: '#/gallery', icon: 'gallery', sub: 'User work · Trending / Newest / Most liked', children: [
      { title: 'Work', href: '#/gallery/gal_bnb_creative', icon: 'gallery', sub: 'Creator, prompt, the flow behind it, buy' },
    ] },
    { title: 'Marketplace', href: '#/marketplace', icon: 'tag', sub: 'Browse · My listings · Purchased', children: [
      { title: 'Listing', href: '#/marketplace/lst_mascot_variant_generator', icon: 'tag', sub: 'Outputs, what’s inside, reviews, buy' },
    ] },
  ] },
  { label: 'Admin', items: [
    { title: 'Admin', href: '#/admin', icon: 'admin', sub: 'Internal — platform_admin only', children: [
      { title: 'Organizations', href: '#/admin/organizations', icon: 'org', children: [
        { title: 'Organization', href: '#/admin/organizations/org_bots_and_bones', icon: 'org' },
      ] },
      { title: 'Users', href: '#/admin/users', icon: 'users' },
      { title: 'Node Catalog', href: '#/admin/nodes', icon: 'node', children: [
        { title: 'Node type', href: '#/admin/nodes/ntp_video_generate', icon: 'node', sub: 'General · I/O · Config · Executor · Versions' },
      ] },
      { title: 'Models & Providers', href: '#/admin/providers', icon: 'model', children: [
        { title: 'Provider', href: '#/admin/providers/prv_fal', icon: 'model' },
      ] },
      { title: 'Assets', href: '#/admin/executions', icon: 'library', sub: 'Support view of the global asset registry' },
      { title: 'Templates', href: '#/admin/templates', icon: 'copy' },
      { title: 'Marketplace', href: '#/admin/marketplace', icon: 'tag', sub: 'Listing moderation, GMV, fees' },
      { title: 'Runs', href: '#/admin/runs', icon: 'runs' },
      { title: 'Failed Runs', href: '#/admin/failed-runs', icon: 'alert' },
      { title: 'Queue', href: '#/admin/queue', icon: 'queue' },
      { title: 'Workers', href: '#/admin/workers', icon: 'cpu' },
      { title: 'Integrations', href: '#/admin/integrations', icon: 'plug' },
      { title: 'Usage & Costs', href: '#/admin/usage', icon: 'chart' },
      { title: 'Feature Flags', href: '#/admin/flags', icon: 'flag' },
      { title: 'Audit Log', href: '#/admin/audit', icon: 'log' },
      { title: 'System Settings', href: '#/admin/system', icon: 'settings' },
      { title: 'Sitemap', href: '#/admin/sitemap', icon: 'sitemap', sub: 'This page' },
      { title: 'Architecture', href: '#/admin/architecture', icon: 'layers' },
      { title: 'Technical Knowledge', href: '#/admin/knowledge', icon: 'library' },
    ] },
  ] },
];

const TONE_BY_DEPTH = ['neutral', 'input', 'model', 'tool', 'output'];

let rowCursor: number;
function layoutNode(node?: any, depth?: any) {
  const children = node.children || [];
  if (!children.length) return { node, depth, row: rowCursor++, children: [] };
  const laid = children.map((c?: any) => layoutNode(c, depth + 1));
  const row = laid.reduce((a?: any, c?: any) => a + c.row, 0) / laid.length;
  return { node, depth, row, children: laid };
}

function build() {
  rowCursor = 0;
  const groups = [];
  for (const g of TREE) {
    const startRow = rowCursor;
    const items = g.items.map((it?: any) => layoutNode(it, 1));
    groups.push({ label: g.label, items, startRow, endRow: rowCursor - 1 });
    rowCursor += 1; // gap between groups
  }
  const itemRows = groups.flatMap((g?: any) => g.items.map((i?: any) => i.row));
  const root: any = { node: { title: 'Content Factory', icon: 'home' }, depth: 0, row: (Math.min(...itemRows) + Math.max(...itemRows)) / 2, children: groups.flatMap((g?: any) => g.items) };
  return { root, groups };
}

function collect(laid?: any, boxes?: any, edges?: any) {
  boxes.push(laid);
  (laid.children || []).forEach((c?: any) => { edges.push([laid, c]); collect(c, boxes, edges); });
}

export function render() {
  const { root, groups } = build();
  const boxes: any[] = []; const edges: any[] = [];
  collect(root, boxes, edges);
  const pos = (b?: any) => ({ x: PAD + b.depth * COL_W, y: PAD + b.row * ROW_H + (ROW_H - BOX_H) / 2, w: BOX_W, h: BOX_H });
  const width = PAD * 2 + (Math.max(...boxes.map((b?: any) => b.depth)) + 1) * COL_W - (COL_W - BOX_W);
  const height = PAD * 2 + (Math.max(...boxes.map((b?: any) => b.row)) + 1) * ROW_H;

  const boxHtml = boxes.map((b?: any) => diagramBox({ ...pos(b), title: b.node.title, sub: b.node.sub, href: b.node.href, icon: b.node.icon, tone: TONE_BY_DEPTH[Math.min(b.depth, 4)] }));
  const groupLabels = groups.filter((g?: any) => g.label).map((g?: any) => diagramGroupLabel(g.label, PAD + COL_W, PAD + g.startRow * ROW_H - 18));
  const edgesSvg = edges.map(([a, c]: any) => edgeH(pos(a), pos(c))).join('');

  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Sitemap', lede: 'Every screen in the prototype, laid out as it is actually reachable. Click any box to open it.' })}
    ${diagramCanvas(html`${boxHtml}${groupLabels}`, edgesSvg, { width, height, label: 'Sitemap of Content Factory' })}
    ${diagramLegend([{ tone: 'neutral', label: 'App root' }, { tone: 'input', label: 'Top-level area' }, { tone: 'model', label: 'Detail screen' }, { tone: 'tool', label: 'Nested detail' }, { tone: 'output', label: 'Deepest level' }])}
  </div>`;
  return { title: 'Sitemap', body };
}
